import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import client, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { fileSrc } from "@/components/common";

export default function Login() {
  const { login } = useApp();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState({});

  useEffect(() => { client.get("/settings/public").then((r) => setBrand(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try { const u = await login(email, password); nav(u.role === "parent" ? "/parent/dashboard" : "/"); }
    catch (e) { setErr(apiError(e)); } finally { setLoading(false); }
  };

  const img = (u, fb) => (u ? (u.startsWith("/api") ? fileSrc(u) : u) : fb);
  const bg = img(brand.login_bg_url, "/login-bg.jpg");
  const logo = img(brand.logo_url, "/logo.png");
  const name = brand.academy_name || "Touzani Academy";

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={bg} alt="academy" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/70 to-[#0F172A]/30" />
        <div className="absolute bottom-12 start-12 text-white max-w-md">
          <img src={logo} alt={name} className="h-24 w-24 object-contain mb-4 drop-shadow-lg" />
          <h2 className="text-4xl font-black font-heading leading-tight">{name}</h2>
          <p className="mt-3 text-slate-300">The complete platform to manage players, finances, training and performance of your football academy.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <img src={logo} alt={name} className="lg:hidden h-16 w-16 object-contain mx-auto mb-4" />
          <h1 className="text-3xl font-extrabold font-heading">Welcome back</h1>
          <p className="text-muted-foreground mt-1 mb-8">Sign in to your academy dashboard</p>
          {err && <div className="mb-4 text-sm text-rose-600 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2" data-testid="login-error">{err}</div>}
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" placeholder="you@academy.com" />
            </div>
            <div>
              <Label className="mb-1.5 block">Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" placeholder="••••••••" />
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full mt-6 bg-emerald-600 hover:bg-emerald-700 text-white h-11" data-testid="login-submit">
            {loading ? <Loader2 className="animate-spin" size={18} /> : "Sign In"}
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-6">Parent? <a href="/parent/login" className="text-emerald-600 font-medium">Go to Parent Portal</a></p>
        </form>
      </div>
    </div>
  );
}
